// Standard library imports
import React, { useEffect, useState } from 'react';
// External library imports
import { Form, Formik } from 'formik';
import XLSX from 'xlsx';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import { UilImport, UilExclamationTriangle, UilBoltAlt } from '@iconscout/react-unicons';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
// Css imports
import Classes from '../../../../styles/AllDevices.module.css';
import AuditClasses from './index.module.css';
// Intenal module imports
import MultipleFilesUpload from '../../../../components/Inputs/FileUpload/MultipleFileUpload';
import { useLoader } from '../../../../hooks';
import { DropdownComponent } from '../../../../components/Inputs/Input';
import { regulationsService } from '../../../../services/regulationsService';
import { momentTimeFormater } from '../../../../utils/timeHelper';
import { DownloadAsExcel } from '../../../../utils/downloadAsExcel';
import { truncateNumber } from '../../../../utils/numberHelper';
import DownloadButton from '../../../../components/Buttons/DownloadButton';
import { setCellData } from '../../../../utils/exceljsHelper';
import { DownloadFileFromBuffer } from '../../../../utils/downloadHelper';
import CustomTooltip from '../../../../components/CustomToolTip/CustomTooltip';
import Table from '../../../../components/Table/Table';
import moment from 'moment';
import _ from 'lodash';
import { RootAuditPQToolService } from '../../../../services/RootAuditPQToolsService';

const TEST_TYPE_FCR_BASED_MARKET = [
    {
        label: 'Static Ramp Test',
        value: 'staticRampTest',
    },
    {
        label: 'Static Linearity Test',
        value: 'staticLinearityTest',
    },
];

const TEST_TYPE_FRR_BASED_MARKET = [
    {
        label: 'Functional Test',
        value: 'functionalTest',
    },
];

const RAMP_TEST_FREQUENCY = {
    inc: {
        t1: 50.1,
        t2: 50.5,
        t3: 50.1,
    },
    dec: {
        t1: 49.9,
        t2: 49.5,
        t3: 49.9,
    },
};

const LINEARLITY_TEST_FREQUENCY = {
    inc: {
        t1: 50.1,
        t2: 50.2,
        t3: 50.3,
        t4: 50.4,
        t5: 50.5,
        t6: 50.4,
        t7: 50.3,
        t8: 50.2,
        t9: 50.1,
    },
    dec: {
        t1: 49.9,
        t2: 49.8,
        t3: 49.7,
        t4: 49.6,
        t5: 49.5,
        t6: 49.6,
        t7: 49.7,
        t8: 49.8,
        t9: 49.9,
    },
};

const MARKET_OPTION = [
    {
        label: 'FCR-D-INC',
        value: 'FCR-D-INC',
    },
    {
        label: 'FCR-D-DEC',
        value: 'FCR-D-DEC',
    },
    {
        label: 'aFRR',
        value: 'aFRR',
    },
];

const fileNamePrefix = 'PQ Analyse';

const PQAnalyser = ({ queryParamsData, updateQueryParams }) => {
    const [startLoader, stopLoader] = useLoader();
    const [testOptions, setTestOption] = useState([]);
    const [selectedMarket, setSelectedMarket] = useState(MARKET_OPTION[0]);
    const [selectedType, setSelectedType] = useState({});
    const [proccessedFile, setProccessedFiles] = useState([]);
    const [valueKey, setValueKey] = useState('VALUE_FREQUENCY');
    const [frequencyOrSetPoint, setFrequencyOrSetPoint] = useState('Frequency');
    const [energinetPQTemplate, setEnerginetPQTemplate] = useState('');

    useEffect(() => {
        getEnerginetTemplate();
        if (selectedMarket?.value?.includes('aFRR') || selectedMarket?.value?.includes('mFRR')) {
            setTestOption(TEST_TYPE_FRR_BASED_MARKET);
            setValueKey('VALUE_SETPOINT');
            setFrequencyOrSetPoint('Set Point');
        } else {
            setTestOption(TEST_TYPE_FCR_BASED_MARKET);
            setValueKey('VALUE_FREQUENCY');
            setFrequencyOrSetPoint('Frequency');
        }
    }, [selectedMarket]);

    const handleUploadClick = async (e) => {
        try {
            startLoader();
            if (
                selectedMarket?.value === 'FCR-D-INC' ||
                selectedMarket?.value === 'FCR-D-DEC' ||
                selectedMarket?.value?.includes('aFRR') ||
                selectedMarket?.value?.includes('mFRR')
            ) {
                const files = Array.from(e.target.files);
                const temp = [];
                const promises = files.map((el) => {
                    const file = el;
                    const energinetFileName = file.name.split('.').join(' Energinet.xlsx.');
                    return new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onload = async (event) => {
                            try {
                                const arrayBuffer = event.target.result;
                                const binaryString = new Uint8Array(arrayBuffer).reduce((data, byte) => {
                                    return data + String.fromCharCode(byte);
                                }, '');
                                const workbook = XLSX.read(binaryString, { type: 'binary' });
                                const sheetName = workbook.SheetNames[0];
                                const excelData = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName]);
                                const column = Object.keys(excelData[0]);
                                if ((column.includes('VALUE_FREQUENCY') || column.includes('VALUE_SETPOINT')) && column.includes('VALUE_POWER') && column.includes('TIMESTAMP')) {
                                    let result = {};
                                    if (selectedType?.value === 'functionalTest') {
                                        result = await functionalTest(excelData, energinetFileName);
                                    } else if (selectedType?.value === 'staticRampTest') {
                                        result = await rampTest(excelData, energinetFileName);
                                    } else {
                                        result = await linearityTest(excelData, energinetFileName);
                                    }
                                    temp.push({
                                        fileName: file.name,
                                        energinetFileName: energinetFileName,
                                        standard: result?.standard || [],
                                        energinet: result?.energinetDataWorkbook || [],
                                        isVerified: result.status ? true : false,
                                        action: result.status ? (
                                            <div className={AuditClasses.ActionButton}>
                                                <div>
                                                    <CustomTooltip content={'Standard Format'}>
                                                        <div onClick={() => handleDownloadLinearity(result.standard, 'standard', file.name)}>
                                                            <UilImport
                                                                size="1.2vw"
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    color: 'var(--color-primary)',
                                                                }}
                                                            />
                                                        </div>
                                                    </CustomTooltip>
                                                </div>
                                                <div>
                                                    <CustomTooltip content={'Energinet Format'}>
                                                        <div onClick={() => handleDownloadLinearity(result.energinetDataWorkbook, 'energinet', energinetFileName)}>
                                                            <UilBoltAlt
                                                                size="1.2vw"
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    color: 'var(--color-primary)',
                                                                }}
                                                            />
                                                        </div>
                                                    </CustomTooltip>
                                                </div>
                                            </div>
                                        ) : (
                                            <div>
                                                <CustomTooltip content={result.error}>
                                                    <div>
                                                        <UilExclamationTriangle
                                                            size="1.2vw"
                                                            style={{
                                                                cursor: 'pointer',
                                                                color: 'var(--color-primary)',
                                                            }}
                                                        />
                                                    </div>
                                                </CustomTooltip>
                                            </div>
                                        ),
                                    });
                                } else {
                                    const error = [];
                                    if (!column.includes('TIMESTAMP')) {
                                        error.push('TIMESTAMP');
                                    }
                                    if (!column.includes('VALUE_POWER')) {
                                        error.push('VALUE_POWER');
                                    }
                                    if (selectedMarket?.value?.includes('aFRR') || selectedMarket?.value?.includes('mFRR')) {
                                        if (!column.includes('VALUE_SETPOINT')) {
                                            error.push('VALUE_SETPOINT');
                                        }
                                    } else {
                                        if (!column.includes('VALUE_FREQUENCY')) {
                                            error.push('VALUE_FREQUENCY');
                                        }
                                    }

                                    temp.push({
                                        fileName: file.name,
                                        energinetFileName: energinetFileName,
                                        standard: [],
                                        energinet: [],
                                        isVerified: false,
                                        action: (
                                            <div>
                                                <CustomTooltip content={`${error.join(', ')} Column missing`}>
                                                    <div>
                                                        <UilExclamationTriangle
                                                            size="1.2vw"
                                                            style={{
                                                                cursor: 'pointer',
                                                                color: 'var(--color-primary)',
                                                            }}
                                                        />
                                                    </div>
                                                </CustomTooltip>
                                            </div>
                                        ),
                                    });
                                }
                                resolve();
                            } catch (error) {
                                reject(error);
                            }
                        };
                        reader.readAsArrayBuffer(file);
                    });
                });
                await Promise.all(promises);
                setProccessedFiles(temp);
            } else {
                toast.warn('Tool is in progress.');
                setProccessedFiles([]);
            }
            stopLoader();
        } catch (err) {
            stopLoader();
            toast.error(`Error while processing file`);
        }
    };

    const checkDuplicateTimestamp = (data) => {
        const temp = [];
        const duplicateTimestamp = [];
        let count = 0;
        data.map((i) => {
            if (temp.includes(i.TIMESTAMP)) {
                count += 1;
                if (!duplicateTimestamp.includes(i.TIMESTAMP)) {
                    duplicateTimestamp.push(i.TIMESTAMP);
                }
            } else {
                temp.push(i.TIMESTAMP);
            }
        });
        return { count, duplicateTimestamp };
    };

    const rampTest = async (data = [], fileName) => {
        let freq = {};
        if (selectedMarket.value === 'FCR-D-INC') {
            freq = RAMP_TEST_FREQUENCY.inc;
        } else if (selectedMarket.value === 'FCR-D-DEC') {
            freq = RAMP_TEST_FREQUENCY.dec;
        }
        if (data.length === 2280) {
            const duplicate = checkDuplicateTimestamp(data);
            if (duplicate.count === 0) {
                const firstTest = data.slice(0, 180).every((obj) => obj.VALUE_FREQUENCY == freq.t1);
                const secondTest = data.slice(180, 1080).every((obj) => obj.VALUE_FREQUENCY == freq.t2);
                const thirdTest = data.slice(1080, 2280).every((obj) => obj.VALUE_FREQUENCY == freq.t3);
                if (!firstTest || !secondTest || !thirdTest) {
                    if (!firstTest) {
                        return {
                            status: false,
                            error: `0-180 range ${freq.t1} frequency not correct`,
                        };
                    } else if (!secondTest) {
                        return {
                            status: false,
                            error: `180-1080 range ${freq.t2} frequency not correct`,
                        };
                    } else if (!thirdTest) {
                        return {
                            status: false,
                            error: `1080-2280 range ${freq.t3} frequency not correct`,
                        };
                    }
                } else {
                    const energinetDataWorkbook = await constructEnerginetFormat(data, fileName);
                    return {
                        standard: data,
                        energinetDataWorkbook: energinetDataWorkbook,
                        status: true,
                    };
                }
            } else {
                return {
                    status: false,
                    error: `${duplicate.count} duplicate timestamps (${duplicate.duplicateTimestamp.join(', ')})`,
                };
            }
        } else {
            if (data.length > 2280) {
                const diff = data.length - 2280;
                return {
                    status: false,
                    error: `${diff} extra timestamps`,
                };
            } else {
                const diff = 2280 - data.length;
                return {
                    status: false,
                    error: `${diff} timestamps missing`,
                };
            }
        }
    };

    const getEnerginetTemplate = async () => {
        if (selectedMarket?.value) {
            const S3_FILE_URL =
                selectedMarket?.value === 'aFRR'
                    ? 'https://truegreen-uploads.s3.eu-central-1.amazonaws.com/afrrEnerginetTemplate.xlsx'
                    : 'https://truegreen-uploads.s3.eu-central-1.amazonaws.com/NewEnerginetTemplate.xlsx';
            RootAuditPQToolService.GetEnerginetTemplateFileBuffer(S3_FILE_URL, startLoader, handleTemplateSuccess, handleError, stopLoader);
        }
    };

    const handleTemplateSuccess = async (data) => {
        const arrayBuffer = await data.arrayBuffer();
        setEnerginetPQTemplate(arrayBuffer);
    };

    const constructEnerginetFormat = async (data, fileName) => {
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(energinetPQTemplate);
        const worksheet = workbook.getWorksheet('Data sheet');
        const worksheet2 = workbook.getWorksheet('Options');
        worksheet2.tables.Tabel95.table.headerRow = true;
        worksheet2.tables.Tabel74.table.headerRow = true;
        worksheet2.tables.Tabel13.table.headerRow = true;

        const cellB4 = worksheet.getCell('B4');
        cellB4.dataValidation = {
            type: 'list',
            formulae: selectedMarket?.value === 'aFRR' ? ["'Options'!$A$4:$A$6"] : ["'Options'!$A$4:$A$8"],
            allowBlank: true,
        };

        const cellB5 = worksheet.getCell('B5');
        cellB5.dataValidation = {
            type: 'list',
            formulae: ["'Options'!$B$4:$B$6"],
            allowBlank: true,
        };

        const cellB6 = worksheet.getCell('B6');
        cellB6.dataValidation = {
            type: 'list',
            formulae: ["'Options'!$C$4:$C$5"],
            allowBlank: true,
        };

        const startRow = 16;
        const cellStyleA = worksheet.getCell('A16').style;
        const cellStyleB = worksheet.getCell('B16').style;
        const cellStyleC = worksheet.getCell('C16').style;
        const cellStyleD = worksheet.getCell('D16').style;
        const cellStyleE = worksheet.getCell('E16').style;

        data.forEach((row, idx) => {
            const date = new Date(row.TIMESTAMP);
            setCellData(worksheet, `A${startRow + idx}`, date, cellStyleA);
            setCellData(worksheet, `B${startRow + idx}`, _.get(row, [valueKey]), cellStyleB);
            setCellData(worksheet, `C${startRow + idx}`, row.VALUE_POWER / 1000, cellStyleC);
            setCellData(worksheet, `D${startRow + idx}`, '', cellStyleD);
            setCellData(worksheet, `E${startRow + idx}`, '', cellStyleE);
        });
        const buffer = await workbook.xlsx.writeBuffer();
        return buffer;
    };

    const linearityTest = async (data = [], fileName) => {
        let freq = {};
        if (selectedMarket.value === 'FCR-D-INC') {
            freq = LINEARLITY_TEST_FREQUENCY.inc;
        } else if (selectedMarket.value === 'FCR-D-DEC') {
            freq = LINEARLITY_TEST_FREQUENCY.dec;
        }
        if (data.length === 1080) {
            const duplicate = checkDuplicateTimestamp(data);
            if (duplicate.count === 0) {
                const test1 = data.slice(0, 120).every((obj) => obj.VALUE_FREQUENCY == freq.t1);
                const test2 = data.slice(120, 240).every((obj) => obj.VALUE_FREQUENCY == freq.t2);
                const test3 = data.slice(240, 360).every((obj) => obj.VALUE_FREQUENCY == freq.t3);
                const test4 = data.slice(360, 480).every((obj) => obj.VALUE_FREQUENCY == freq.t4);
                const test5 = data.slice(480, 600).every((obj) => obj.VALUE_FREQUENCY == freq.t5);
                const test6 = data.slice(600, 720).every((obj) => obj.VALUE_FREQUENCY == freq.t6);
                const test7 = data.slice(720, 840).every((obj) => obj.VALUE_FREQUENCY == freq.t7);
                const test8 = data.slice(840, 960).every((obj) => obj.VALUE_FREQUENCY == freq.t8);
                const test9 = data.slice(960, 1080).every((obj) => obj.VALUE_FREQUENCY == freq.t9);

                if (!test1 || !test2 || !test3 || !test4 || !test5 || !test6 || !test7 || !test8 || !test9) {
                    if (!test1) {
                        return {
                            status: false,
                            error: `0-120 range ${freq.t1} frequency not correct`,
                        };
                    } else if (!test2) {
                        return {
                            status: false,
                            error: `120-240 range ${freq.t2} frequency not correct`,
                        };
                    } else if (!test3) {
                        return {
                            status: false,
                            error: `240-360 range ${freq.t3} frequency not correct`,
                        };
                    } else if (!test4) {
                        return {
                            status: false,
                            error: `360-480 range ${freq.t4} frequency not correct`,
                        };
                    } else if (!test5) {
                        return {
                            status: false,
                            error: `480-600 range ${freq.t5} frequency not correct`,
                        };
                    } else if (!test6) {
                        return {
                            status: false,
                            error: `600-720 range ${freq.t6} frequency not correct`,
                        };
                    } else if (!test7) {
                        return {
                            status: false,
                            error: `720-840 range ${freq.t7} frequency not correct`,
                        };
                    } else if (!test8) {
                        return {
                            status: false,
                            error: `840-960 range ${freq.t8} frequency not correct`,
                        };
                    } else if (!test9) {
                        return {
                            status: false,
                            error: `960-1080 range ${freq.t9} frequency not correct`,
                        };
                    }
                } else {
                    const energinetDataWorkbook = await constructEnerginetFormat(data, fileName);
                    return {
                        standard: data,
                        energinetDataWorkbook: energinetDataWorkbook,
                        status: true,
                    };
                }
            } else {
                return {
                    status: false,
                    error: `${duplicate.count} duplicate timestamps (${duplicate.duplicateTimestamp.join(', ')})`,
                };
            }
        } else {
            if (data.length > 1080) {
                const diff = data.length - 1080;
                return {
                    status: false,
                    error: `${diff} extra timestamps`,
                };
            } else {
                const diff = 1080 - data.length;
                return {
                    status: false,
                    error: `${diff} timestamps missing`,
                };
            }
        }
    };

    const functionalTest = async (data = [], fileName) => {
        if (selectedMarket?.value?.includes('aFRR') || selectedMarket?.value?.includes('mFRR')) {
            if (data.length === 3600) {
                const duplicate = checkDuplicateTimestamp(data);
                if (duplicate.count === 0) {
                    const firstTest = data.slice(0, 300).every((obj) => obj.VALUE_SETPOINT <= 100);
                    const secondTest = data.slice(300, 1200).every((obj) => obj.VALUE_SETPOINT === 100);
                    const thirdTest = data.slice(1200, 1500).every((obj) => obj.VALUE_SETPOINT >= 0);
                    const fourTest = data.slice(1500, 2100).every((obj) => obj.VALUE_SETPOINT === 0);
                    const fifthTest = data.slice(2100, 2400).every((obj) => obj.VALUE_SETPOINT <= 100);
                    const sixTest = data.slice(2400, 2700).every((obj) => obj.VALUE_SETPOINT >= 0);
                    const sevenTest = data.slice(2700, 3000).every((obj) => obj.VALUE_SETPOINT <= 50 && obj.VALUE_SETPOINT >= 0);
                    const eightTest = data.slice(3000, 3300).every((obj) => obj.VALUE_SETPOINT === 50);
                    const nineTest = data.slice(3300, 3600).every((obj) => obj.VALUE_SETPOINT <= 50 && obj.VALUE_SETPOINT >= 0);

                    if (!firstTest) {
                        return {
                            status: false,
                            error: `0-300 range set point not correct`,
                        };
                    } else if (!secondTest) {
                        return {
                            status: false,
                            error: `300-1200 range set point not correct`,
                        };
                    } else if (!thirdTest) {
                        return {
                            status: false,
                            error: `1200-1500 range set point not correct`,
                        };
                    } else if (!fourTest) {
                        return {
                            status: false,
                            error: `1500-2100 range set point not correct`,
                        };
                    } else if (!fifthTest) {
                        return {
                            status: false,
                            error: `2100-2400 range set point not correct`,
                        };
                    } else if (!sixTest) {
                        return {
                            status: false,
                            error: `2400-2700 range set point not correct`,
                        };
                    } else if (!sevenTest) {
                        return {
                            status: false,
                            error: `2700-3000 range set point not correct`,
                        };
                    } else if (!eightTest) {
                        return {
                            status: false,
                            error: `3000-3300 range set point not correct`,
                        };
                    } else if (!nineTest) {
                        return {
                            status: false,
                            error: `3300-3600 range set point not correct`,
                        };
                    } else {
                        const energinetDataWorkbook = await constructEnerginetFormat(data, fileName);
                        return {
                            standard: data,
                            energinetDataWorkbook: energinetDataWorkbook,
                            status: true,
                        };
                    }
                } else {
                    return {
                        status: false,
                        error: `${duplicate.count} duplicate timestamps (${duplicate.duplicateTimestamp.join(', ')})`,
                    };
                }
            } else {
                if (data.length > 3600) {
                    const diff = data.length - 3600;
                    return {
                        status: false,
                        error: `${diff} extra timestamps`,
                    };
                } else {
                    const diff = 3600 - data.length;
                    return {
                        status: false,
                        error: `${diff} timestamps missing`,
                    };
                }
            }
        } else {
            toast.error('Please select aFRR market.');
        }
    };
    const handleDownloadLinearity = (data, type = 'standard', fileName) => {
        if (type === 'energinet') {
            DownloadFileFromBuffer(data, `${fileNamePrefix} ${fileName}`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
        } else {
            downloadFile(data, fileName);
        }
    };

    const downloadZipFile = async (type) => {
        startLoader();
        const zip = new JSZip();
        proccessedFile.map((item) => {
            if (item.isVerified) {
                if (type == 'standard') {
                    const headers = ['Date (CET)', 'Time (CET)', frequencyOrSetPoint, 'Power'];
                    const worksheetData = [
                        headers,
                        ...item.standard.map((i) => [
                            momentTimeFormater(i.TIMESTAMP).format('YYYY-MM-DD'),
                            momentTimeFormater(i.TIMESTAMP).format('HH:mm:ss'),
                            truncateNumber(_.get(i, [valueKey]), 3),
                            truncateNumber(i.VALUE_POWER, 3),
                        ]),
                    ];
                    const ws = XLSX.utils.aoa_to_sheet(worksheetData);
                    const wb = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
                    const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
                    const excelBlob = new Blob([new Uint8Array(buffer)], {
                        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    });
                    zip.file(item.fileName, excelBlob);
                } else {
                    const excelBlob = new Blob([item.energinet], {
                        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    });
                    zip.file(item.energinetFileName, excelBlob);
                }
            }
        });
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        saveAs(zipBlob, `${fileNamePrefix} ${type} format.zip`);
        stopLoader();
    };

    const downloadFile = (data, fileName) => {
        const formatedData = data.map((i) => {
            return {
                date: momentTimeFormater(i.TIMESTAMP).format('YYYY-MM-DD'),
                time: momentTimeFormater(i.TIMESTAMP).format('HH:mm:ss'),
                frequency: truncateNumber(_.get(i, [valueKey]), 3),
                power: truncateNumber(i.VALUE_POWER, 3),
            };
        });
        const headers = ['Date (CET)', 'Time (CET)', frequencyOrSetPoint, 'Power'];
        DownloadAsExcel(formatedData, `${fileNamePrefix} ${fileName}`, headers);
    };

    const handleError = (err) => {
        console.log(err);
        if (err && err.response) toast.error(err.response.data.message);
    };

    return (
        <div>
            <div className={AuditClasses.Filter}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div className={AuditClasses.FilterContainer}>
                        <div></div>
                        <div>
                            <div className={Classes.FieldControl}>
                                <label>Select Market</label>
                                <DropdownComponent
                                    name="market"
                                    options={MARKET_OPTION}
                                    defaultValue={selectedMarket}
                                    onChange={(e) => {
                                        if (e.value !== selectedMarket?.value) {
                                            setSelectedMarket(e);
                                            setProccessedFiles([]);
                                        }
                                    }}
                                />
                            </div>
                        </div>
                        <div>
                            <div className={Classes.FieldControl}>
                                <label>Select Type</label>
                                <DropdownComponent
                                    name="type"
                                    options={testOptions}
                                    defaultValue={testOptions[0]}
                                    onChange={(e) => {
                                        if (e.value !== selectedType?.value) {
                                            setSelectedType(e);
                                            setProccessedFiles([]);
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                    <div className={AuditClasses.FilterContainer}></div>
                </div>
            </div>
            <div className={Classes.AllDevices}>
                <div className={AuditClasses.UploadFileContainer}>
                    <Formik initialValues={{}} enableReinitialize>
                        {({ errors, touched, values, isValidating, ...props }) => {
                            return (
                                <Form>
                                    <div className={AuditClasses.FileContainer}>
                                        <div className={Classes.InputContainer}>
                                            <div className={AuditClasses.DatePickerLabel} style={{ width: '35vw' }}>
                                                <label>Upload file</label>
                                                <MultipleFilesUpload
                                                    name="file"
                                                    id="file"
                                                    accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                                                    onChange={handleUploadClick}
                                                    multiple
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </Form>
                            );
                        }}
                    </Formik>
                    {proccessedFile?.length ? (
                        <div>
                            <div className={`${AuditClasses.FileContainer} ${AuditClasses.DownloadButtonControl}`}>
                                <DownloadButton size={'medium'} text={'All files - Standard Format'} onClick={() => downloadZipFile('standard')} />
                                <DownloadButton size={'medium'} text={'All files - Energinet Format'} onClick={() => downloadZipFile('energinet')} />
                            </div>
                            <div style={{ minWidth: '35vw' }}>
                                <Table head={['File', 'Action']} keys={['fileName', 'action']} data={proccessedFile} />
                            </div>
                        </div>
                    ) : (
                        ''
                    )}
                </div>
            </div>
        </div>
    );
};

export default PQAnalyser;
